/**
 * 附加费率快照：把「门店配置 + 门店时区 + 服务器时钟的现在」变成 Vuex 里的 `app.paymentSurcharge`。
 *
 * 后端在**建单那一刻**取费率（审计 00-backend-spec §4.4）。以前前端只在路由变化时按手机时钟快照一次，
 * 顾客在购物车里停留时跨过时段边界（晚市开始 / 结束、午夜换星期），报上去的 amount 就和后端不一样，被 `##9##` 拒。
 * 现在：App.vue 拿到 get-venue 后登记配置并刷新一次（给页面显示用），每个建单请求再用 `orderAmountNow` 当场重判一次；
 * 购物车页开着时每 30 秒 / 切回前台再重判（`startSurchargeAutoRefresh`）；多店按各成员店自己的配置重判
 * （`refreshSubGroupSurcharges` / `subGroupOrderAmountNow`）。
 */
import { resolveCurrentSurcharge, resolveMerchantRate } from '@/utils/surchargeRate'
import { venueNowParts, serverNowMs, venueTimeZoneOf } from '@/utils/venueClock'

/**
 * 一家门店的附加费判定依据：get-venue 响应里的附加费配置原样 + 门店时区（+ 是哪家店）。
 * App.vue 登记当前店、多店补拉成员店都用它，保证同一份 get-venue 得出同一份依据。
 * @param {{ id?: string, settings?: { surcharges?: object } }} venue get-venue 的 data
 * @returns {{ venueId: string|null, surcharges: object|null, timeZone: string }}
 */
export function surchargeSourceOf(venue) {
  return {
    venueId: venue?.id ?? null,
    surcharges: venue?.settings?.surcharges ?? null,
    timeZone: venueTimeZoneOf(venue)
  }
}

/**
 * 按「现在」重判附加费率并写进 store。还没登记过门店配置时什么都不做（沿用现有快照）。
 * @param {import('vuex').Store} store
 * @param {number} [nowMs] 默认取服务器时钟
 * @returns {number|undefined} 新的费率（小数），没登记配置时 undefined
 */
export function refreshSurchargeSnapshot(store, nowMs = serverNowMs()) {
  const source = store.state.app.surchargeSource
  if (!source) return undefined
  const { rate, settings } = resolveCurrentSurcharge(source.surcharges, venueNowParts(nowMs, source.timeZone))
  store.commit('savePaymentSurchargeSettings', rate > 0 ? settings : '')
  store.commit('savePaymentSurcharge', rate)
  // 老字段（拼写照旧、百分数），后端覆写不采信；保留只是为了不改请求形状
  store.commit('addDetailsProp', { prop: 'surchageRate', val: rate * 100 })
  // 拼写正确、与后端同单位（小数）的一份。后端 selfValidate 会覆写它，不影响钱；
  // 但 ##9## 诊断日志的 clientSurchargeRate 读的就是它（以前老顾客端只报拼错的字段，那一栏永远是 null）
  store.commit('addDetailsProp', { prop: 'surchargeRate', val: rate })
  return rate
}

/**
 * 建单请求里的 amount：先按建单这一刻重判附加费率，再取应付总额。
 * @param {import('vuex').Store} store
 * @param {string} venueId
 * @param {number} [nowMs] 默认取服务器时钟
 * @returns {number}
 */
export function orderAmountNow(store, venueId, nowMs = serverNowMs()) {
  refreshSurchargeSnapshot(store, nowMs)
  return store.getters.realPrice(venueId)
}

/**
 * 多店合单：按每家成员店**自己**的配置重判费率，写回 `subGroupCartList[店].cartInfo`（review 第 1 轮 #5）。
 * cartInfo 的费率以前只在改这家的购物车时按「当时正在逛的店」快照一次；顾客加完购物车停一会儿再结账，
 * 跨过时段边界后屏幕上的合计与后端建单金额对不上（多店后端不比对金额、照收，顾客看到的数字一下子跳了）。
 * 加购时 cartInfo 同时存下 `surchargeSource`（utils/mixin.js / 多店购物车页），这里用它按「现在」重判；
 * 没有判定依据的（上线前加购的、在别家店页面加的推荐品）要先 ensureSubGroupSurchargeSources 补拉，否则保留原快照。
 * @param {import('vuex').Store} store
 * @param {number} [nowMs] 默认取服务器时钟
 * @returns {number} 重判了几家
 */
export function refreshSubGroupSurcharges(store, nowMs = serverNowMs()) {
  const cartList = store.state.venueHome.subGroupCartList || {}
  let n = 0
  Object.keys(cartList).forEach((venueId) => {
    const source = cartList[venueId] && cartList[venueId].cartInfo && cartList[venueId].cartInfo.surchargeSource
    // 没有依据的（上线前加购 / 别家店页面加的推荐品）先由 ensureSubGroupSurchargeSources 补拉，这里跳过
    if (!source) return
    const { rate, settings } = resolveCurrentSurcharge(source.surcharges, venueNowParts(nowMs, source.timeZone))
    store.commit('saveSubGroupVenueSurcharge', { venueId, paymentSurcharge: rate, paymentSurchargeSettings: rate > 0 ? settings : '' })
    n++
  })
  return n
}

/**
 * 往多店购物车里某家成员店加东西时存进 cartInfo 的费率与判定依据（Codex 第 2 轮 #4）。
 * app 里的依据是**当前正在逛的那家**（App.vue 登记时带上 venueId）；在 A 店页面加 B 店的推荐品时不能照抄 A 的，
 * 否则 B 按 A 的附加费 / 刷卡费算。不是当前店的：费率先按 0、依据留空，由 ensureSubGroupSurchargeSources 补拉 B 自己的设置。
 * （addNewProductToSubgroupCart 只在这家店的格子还不存在时才用传进去的 cartInfo，已有的格子不会被覆盖。）
 * @param {import('vuex').Store} store
 * @param {string} venueId 商品所属的成员店
 * @returns {{ paymentSurcharge: number, paymentSurchargeSettings: any, merchantFee: number, surchargeSource: object|null }}
 */
export function subGroupCartInfoFor(store, venueId) {
  const app = store.state.app
  const source = app.surchargeSource
  if (source && source.venueId === venueId) {
    return {
      paymentSurcharge: app.paymentSurcharge ?? 0,
      paymentSurchargeSettings: app.paymentSurchargeSettings ?? [],
      // 还没拿到门店配置时按 0（与后端 Venue::getMerchantRate 的「开关没开」同义）
      merchantFee: app.merchantFee || 0,
      surchargeSource: source
    }
  }
  return { paymentSurcharge: 0, paymentSurchargeSettings: '', merchantFee: 0, surchargeSource: null }
}

/**
 * 给多店购物车里**没有判定依据**的成员店补拉门店设置（Codex 第 2 轮 #3 / #4）：
 *   - 本次上线前加购的条目（cartInfo 里只有当时的费率快照）；
 *   - 在别家店页面加的推荐品（subGroupCartInfoFor 留空的）。
 * 拉到后写回这家的判定依据与刷卡费率（resolveMerchantRate，与后端 getMerchantRate 同义），之后 refreshSubGroupSurcharges /
 * subGroupOrderAmountNow 就能按它重判。拉失败的保留原快照、不抛错（多店后端不比对金额，照自己的算法收）。
 * ⚠️ fetchVenue 不能走 utils/request 的实例：requestMixin 收到别家店的 get-venue 会把当前店的状态整个重置
 * （`data.id !== store.state.venueId` 那一段），调用方传 api/venueQuiet.js 的 getVenueQuietly。
 * @param {import('vuex').Store} store
 * @param {{ fetchVenue: (venueId: string) => Promise<object|null> }} opts
 * @returns {Promise<number>} 补上了几家
 */
export async function ensureSubGroupSurchargeSources(store, { fetchVenue }) {
  const cartList = store.state.venueHome.subGroupCartList || {}
  const missing = Object.keys(cartList).filter((venueId) => {
    const cell = cartList[venueId]
    return cell && Array.isArray(cell.cart) && cell.cart.length > 0 && !(cell.cartInfo && cell.cartInfo.surchargeSource)
  })
  const done = await Promise.all(missing.map(async (venueId) => {
    try {
      const venue = await fetchVenue(venueId)
      if (!venue || !venue.settings) return false
      store.commit('saveSubGroupVenueSurchargeSource', {
        venueId,
        surchargeSource: surchargeSourceOf({ ...venue, id: venue.id ?? venueId }),
        merchantFee: resolveMerchantRate(venue.settings.options)
      })
      return true
    } catch (e) {
      return false
    }
  }))
  return done.filter(Boolean).length
}

/**
 * 多店建单请求里的 amount：先按建单这一刻重判各成员店的费率，再取合计（与单店的 orderAmountNow 对应）。
 * 调用前先 `await ensureSubGroupSurchargeSources(...)`，否则缺判定依据的成员店仍按旧快照。
 * @param {import('vuex').Store} store
 * @param {string} venueSubGroupId
 * @param {number} [nowMs] 默认取服务器时钟
 * @returns {number}
 */
export function subGroupOrderAmountNow(store, venueSubGroupId, nowMs = serverNowMs()) {
  refreshSubGroupSurcharges(store, nowMs)
  return store.getters.subGroupCartRealPrice(venueSubGroupId)
}

/** 购物车页开着时重判的间隔（review 第 1 轮 #4：清单要求 ≤ 30 秒） */
export const SURCHARGE_REFRESH_INTERVAL_MS = 30000

/**
 * 页面开着期间让显示的附加费跟上时钟（review 第 1 轮 #4）：每 30 秒、以及页面切回前台时按服务器时钟重判一次。
 * 以前只在切路由时刷新，顾客停在购物车里跨过时段边界，屏幕上的合计还是旧的，点下单时（orderAmountNow 当场重判）
 * 付款页金额才跳。只有显示前端合计的页面需要：单店购物车、多店购物车（下单确认页 / 付款页显示的是后端金额）。
 * @param {import('vuex').Store} store
 * @param {{ subGroup?: boolean, refresh?: Function, intervalMs?: number, doc?: Document }} [opts]
 *   subGroup：多店购物车，另外重判各成员店；refresh / doc 供测试注入
 * @returns {() => void} 停止（页面 beforeDestroy 里调）
 */
export function startSurchargeAutoRefresh(store, opts = {}) {
  const {
    subGroup = false,
    intervalMs = SURCHARGE_REFRESH_INTERVAL_MS,
    doc = typeof document !== 'undefined' ? document : null
  } = opts
  const refresh = opts.refresh || (() => {
    refreshSurchargeSnapshot(store)
    if (subGroup) refreshSubGroupSurcharges(store)
  })
  const timer = setInterval(refresh, intervalMs)
  const onVisibilityChange = () => {
    if (doc && doc.visibilityState === 'hidden') return
    refresh()
  }
  if (doc) doc.addEventListener('visibilitychange', onVisibilityChange)
  return () => {
    clearInterval(timer)
    if (doc) doc.removeEventListener('visibilitychange', onVisibilityChange)
  }
}
